import { Callout } from "@blueprintjs/core";
import orderBy from "lodash/orderBy";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { MeetingBasics } from "@/components/pieces/coaching/meetings/MeetingBasics";
import { CARD_CLASSES, LIGHT_GRAY, LIGHT_GREEN } from "@/css/constants";
import {
  ComponentStatus,
  ComponentTypeEnum,
  GetParticipantSessionResponseQuery,
  MeetingStatus,
  useGetParticipantSessionResponseQuery,
} from "@/graphql";
import { ResponseComponent } from "@/types/programs";
import { NonNullableArrayItem } from "@/types/utils";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";

import { ProgramComponentHeader } from "./program-component/ProgramComponentHeader";

interface StatCardProps {
  title: string;
  value: string | number;
  backgroundColor: string;
  color: string;
  textColor?: string;
  icon?: React.ReactNode;
}

function StatCard({
  title,
  value,
  backgroundColor,
  color,
  icon,
  textColor,
}: StatCardProps) {
  return (
    <Box
      css={{
        backgroundColor,
        borderRadius: "10px",
        padding: "24px",
        flex: 1,
        minWidth: "200px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Box css={{ color: color, fontSize: "16px", fontWeight: 400 }}>
        {title}
      </Box>
      <Flex alignItems="center" css={{ gap: "12px" }}>
        {icon}
        <Box
          css={{
            color: textColor || color,
            fontSize: "32px",
            fontWeight: 600,
            lineHeight: "1em",
          }}
        >
          {value || 0}
        </Box>
      </Flex>
    </Box>
  );
}

interface ProgressIndicatorProps {
  usedSessions: number;
  totalSessions: number;
}

function ProgressIndicator({
  usedSessions,
  totalSessions,
}: ProgressIndicatorProps) {
  return (
    <Flex alignItems="center" style={{ gap: "10px" }}>
      <Flex alignItems="center" style={{ gap: "4px" }}>
        {Array.from({ length: totalSessions }).map((_, index) => (
          <Box
            key={index}
            css={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor:
                index < usedSessions ? "rgba(39, 174, 96, 0.7)" : "#E2E2E2",
            }}
          />
        ))}
      </Flex>
      <Box
        css={{
          color: "#242424",
          fontSize: "16px",
          lineHeight: "1.6em",
          flex: 1,
        }}
      >
        {usedSessions} of {totalSessions} sessions used
      </Box>
    </Flex>
  );
}

export function ProgramCoachingResponse({
  component,
  participantId,
}: {
  component: ResponseComponent;
  participantId: string;
}) {
  const { data } = useGetParticipantSessionResponseQuery({
    variables: {
      programComponentId: component.id,
      participantId,
    },
    fetchPolicy: "no-cache",
  });

  const participantSessionResponse = data?.participantSessionResponse;
  const programComponents = ensureNonNullArray(
    participantSessionResponse?.programComponents,
  );

  const {
    completedSessionsCount,
    scheduledSessionsCount,
    missedSessionsCount,
  } = calculateStats(programComponents);

  const stats: StatCardProps[] = [
    {
      title: "Total Spent",
      value: participantSessionResponse?.totalSpent || "0",
      backgroundColor: LIGHT_GRAY,
      color: "#778397",
      textColor: "#000",
      icon: <EmbedSvg src={CreditSVG} width={32} height={32} />,
    },
    {
      title: "Completed Sessions",
      value: completedSessionsCount,
      backgroundColor: LIGHT_GREEN,
      color: "#0D8050",
    },
    {
      title: "Scheduled Sessions",
      value: scheduledSessionsCount,
      backgroundColor: "#E9F1FB",
      color: "#215DB0",
    },
    {
      title: "Missed",
      value: missedSessionsCount,
      backgroundColor: "#F5E6E8",
      color: "#C61D1D",
    },
  ];

  return (
    <Box>
      <Flex mt={24} flexWrap="wrap" css={{ gap: "10px" }}>
        {stats.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </Flex>
      {orderBy(programComponents, "targetStartDate").map((programComponent) => {
        const isIntro =
          programComponent.type === ComponentTypeEnum.OneOneCoachingIntro;
        return (
          <Box flex={1} px={32} py={32} my={24} className={CARD_CLASSES}>
            <ProgramComponentHeader
              component={programComponent}
              isEditable={false}
            />
            {programComponent.status === ComponentStatus.Missed && (
              <Callout
                intent="danger"
                icon="cross-circle"
                style={{ marginTop: 24 }}
              >
                Participant missed this session
              </Callout>
            )}
            {[ComponentStatus.Ongoing, ComponentStatus.Upcoming].includes(
              programComponent.status,
            ) && (
              <Callout intent="primary" icon="time" style={{ marginTop: 24 }}>
                Participant has until{" "}
                <b>{formatDate(programComponent.targetEndDate, "MMM d")} </b>
                to schedule this session
              </Callout>
            )}
            <Box
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                marginTop: 24,
              }}
            >
              {ensureNonNullArray(programComponent.meetings).map((meeting) => (
                <Box
                  css={{
                    border: "1px solid #E2E2E2",
                    borderRadius: "8px",
                    padding: "24px",
                  }}
                >
                  <MeetingBasics
                    meeting={meeting}
                    large={false}
                    showMessageButton={false}
                    showMenu={false}
                    readOnly={true}
                  />
                </Box>
              ))}
            </Box>

            {isIntro && (
              <Box css={{ marginTop: "24px" }}>
                <ProgressIndicator
                  usedSessions={
                    programComponent.meetings?.filter(
                      (meeting) => meeting.status !== MeetingStatus.Canceled,
                    ).length || 0
                  }
                  totalSessions={programComponent.introSessionCount || 0}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

type Component = NonNullableArrayItem<
  NonNullable<
    GetParticipantSessionResponseQuery["participantSessionResponse"]
  >["programComponents"]
>;

const calculateStats = (programComponents: Component[]) => {
  const completedSessionsCount = programComponents.reduce(
    (acc, programComponent) => {
      const completedCount =
        programComponent?.meetings?.filter(
          (meeting) => meeting.status === MeetingStatus.Complete,
        ).length ?? 0;
      return acc + completedCount;
    },
    0,
  );

  const scheduledSessionsCount = programComponents.reduce(
    (acc, programComponent) => {
      const scheduledCount =
        programComponent?.meetings?.filter(
          (meeting) =>
            meeting?.status &&
            [
              MeetingStatus.Scheduling,
              MeetingStatus.Ongoing,
              MeetingStatus.Soon,
              MeetingStatus.Upcoming,
            ].includes(meeting.status),
        ).length ?? 0;
      return acc + scheduledCount;
    },
    0,
  );

  const missedSessionsCount = programComponents.reduce(
    (acc, programComponent) => {
      const missedCount =
        programComponent.status === ComponentStatus.Missed ? 1 : 0;
      return acc + missedCount;
    },
    0,
  );

  return {
    completedSessionsCount,
    scheduledSessionsCount,
    missedSessionsCount,
  };
};
